/**
 * Common file upload utilities for GCS presigned URL flow.
 * Used by: Materiality document analysis, Entity scoping (trial balance), and future file uploads.
 *
 * Flow: initUpload(Batch) → uploadToGCS → completeUpload
 */

import axiosInstance from '../api/axios';

const FILES_API_BASE = '/api/files';

/**
 * Initialize a batch of file uploads; returns presigned URLs and file_refs for each file.
 * @param {File[]} files - Array of File objects
 * @param {string} token - Auth token (e.g. userHash)
 * @param {string} moduleUpload - One of MODULE_UPLOAD values (e.g. MATERIALITY_ASSESSMENT, ENTITY_SCOPING)
 * @returns {Promise<{ files: Array<{ upload_url, file_ref, content_type, filename?, size_bytes? }> }>}
 */
export async function initUploadBatch(files, token, moduleUpload) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = token;
  const res = await axiosInstance.post(`${FILES_API_BASE}/init-upload`, buildInitUploadBody(files, moduleUpload), {
    headers,
  });
  if (res.data?.success === false) {
    throw new Error(res.data?.message || 'Init upload failed');
  }
  return res.data;
}

/**
 * Build init-upload request body — same schema for single-file and batch (materiality, entity scoping, etc.).
 * POST body: { "files": [ { "filename", "content_type", "size_bytes", "module_upload" }, ... ] }
 */
export function buildInitUploadBody(files, moduleUpload) {
  return {
    files: files.map((f) => ({
      filename: f.name,
      content_type: f.type || 'application/octet-stream',
      size_bytes: f.size,
      module_upload: moduleUpload,
    })),
  };
}

/**
 * Initialize a single file upload; same payload shape as batch (uses `files` array with one entry).
 * @param {File} file - Single File object
 * @param {string} token - Auth token
 * @param {string} moduleUpload - One of MODULE_UPLOAD values
 * @returns {Promise<{ upload_url, file_ref, content_type, expires_at }>}
 */
export async function initUpload(file, token, moduleUpload) {
  const res = await initUploadBatch([file], token, moduleUpload);
  const initFiles = res.files || [];
  const normalizedFile = initFiles[0];
  if (!normalizedFile?.upload_url || !normalizedFile?.file_ref) {
    throw new Error(`Init upload failed for file: ${file.name}`);
  }
  return normalizedFile;
}

/**
 * Upload file bytes to GCS using the presigned URL (PUT).
 * Uses the File/Blob as the body so the runtime can stream it instead of buffering a full ArrayBuffer copy.
 * @param {string} uploadUrl - Presigned URL from init
 * @param {File} file - File object
 * @param {string} contentType - Content-Type for the request
 */
export async function uploadToGCS(uploadUrl, file, contentType) {
  if (!contentType) {
    throw new Error(`Signed upload missing content_type for file: ${file.name}`);
  }
  let response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });
  } catch (networkError) {
    throw new Error(`GCS upload failed: network/CORS error (${networkError?.message || 'unknown'})`);
  }
  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    const errorSuffix = errorBody ? ` - ${errorBody.slice(0, 200)}` : '';
    throw new Error(`GCS upload failed: ${response.status}${errorSuffix}`);
  }
}

/**
 * Complete the upload on the backend; returns uuid for the stored file.
 * @param {string} fileRef - file_ref from init
 * @param {string} filename - Original filename
 * @param {string} contentType - Content type
 * @param {number} sizeBytes - File size in bytes
 * @param {string} token - Auth token
 * @param {string} moduleUpload - One of MODULE_UPLOAD values
 * @returns {Promise<{ uuid: string }>}
 */
export async function completeUpload(fileRef, filename, contentType, sizeBytes, token, moduleUpload) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = token;
  const res = await axiosInstance.post(`${FILES_API_BASE}/complete-upload`, {
    file_ref: fileRef,
    filename,
    content_type: contentType,
    size_bytes: sizeBytes,
    module_upload: moduleUpload,
  }, {
    headers,
  });
  if (res.data?.success === false) {
    throw new Error(res.data?.message || 'Complete upload failed');
  }
  const data = res.data;
  if (!data?.uuid) {
    throw new Error('Complete upload did not return a file uuid');
  }
  return data;
}

/**
 * Full flow: init (batch) → upload each to GCS → complete each. Returns entries with file_id (uuid).
 * @param {File[]} files - Array of File objects
 * @param {string} token - Auth token
 * @param {string} moduleUpload - One of MODULE_UPLOAD values
 * @returns {Promise<Array<{ filename, file_id, content_type, size_bytes }>>}
 */
export async function uploadFilesAndComplete(files, token, moduleUpload) {
  if (!files?.length) return [];
  const initResponse = await initUploadBatch(files, token, moduleUpload);
  const initFiles = initResponse.files || [];
  if (initFiles.length !== files.length) {
    throw new Error('Init upload returned unexpected number of URLs');
  }
  await Promise.all(
    initFiles.map(async (initFile, index) => {
      if (!initFile?.upload_url || !initFile?.file_ref) {
        throw new Error(`Init upload failed for file: ${files[index]?.name || index}`);
      }
      await uploadToGCS(initFile.upload_url, files[index], initFile.content_type);
    })
  );
  const uploadedEntries = initFiles.map((initFile, index) => ({
    filename: files[index]?.name || initFile?.filename || `file_${index + 1}`,
    file_ref: initFile.file_ref,
    content_type: initFile.content_type || files[index]?.type || 'application/octet-stream',
    size_bytes: files[index]?.size ?? initFile.size_bytes ?? 0,
  }));
  const completeResponses = await Promise.all(
    uploadedEntries.map((item) =>
      completeUpload(item.file_ref, item.filename, item.content_type, item.size_bytes, token, moduleUpload)
    )
  );
  return uploadedEntries.map((entry, i) => ({
    ...entry,
    file_id: completeResponses[i]?.uuid,
  }));
}
